import type { ActivitySurface } from './activity.model'

/** Which `KeyboardEvent` property the surface's own handler matches on. */
export type ShortcutMatchMode = 'code' | 'key'

export interface SurfaceShortcuts {
  readonly mode: ShortcutMatchMode
  /** The editors' handlers let Shift+R act as R; the group screen's do not. */
  readonly ignoresShift: boolean
  readonly keys: ReadonlySet<string>
}

const EDITOR_COMMON = ['Space', 'Enter', 'Escape', 'Delete', 'Backspace', '?', 'Ctrl+Z', 'Ctrl+Shift+Z', 'Ctrl+Y']

const EDITING_KEYS = ['P', 'V', 'M', 'D', 'C', 'S', 'H', ...EDITOR_COMMON]
/** The Segment editing / Quick Edit work pane's navigation: pager, Next, zoom. */
const EDIT_PANE_NAV_KEYS = [',', '.', 'J', 'Z']
/**
 * Quick Edit: the editing tools plus its work pane — the Changes (E) tab and the
 * navigation keys (no Roads tab, so no R; no attribute form, so no Ctrl+Enter).
 */
const QUICK_EDIT: SurfaceShortcuts = {
  mode: 'code', ignoresShift: true, keys: new Set([...EDITING_KEYS, 'E', ...EDIT_PANE_NAV_KEYS]),
}
/** Segment editing: the editing tools plus the work pane's Roads (R) and Changes (E) tabs, its navigation and Apply. */
const SEGMENT_EDITING: SurfaceShortcuts = {
  mode: 'code', ignoresShift: true, keys: new Set([...EDITING_KEYS, 'R', 'E', ...EDIT_PANE_NAV_KEYS, 'Ctrl+Enter']),
}
const MARKUP: SurfaceShortcuts = {
  mode: 'code', ignoresShift: true,
  keys: new Set([
    'P', 'V', 'M', 'R', 'O', 'G', 'L', 'T', 'N', 'I', 'A', 'S', 'C', 'H',
    // The work pane: pager, Next to do, footer answers, Quick Edit flag, zoom, Apply.
    ',', '.', 'J', '1', '2', '3', 'Q', 'Z', 'Ctrl+Enter',
    ...EDITOR_COMMON,
  ]),
}
const GROUP_REVIEW: SurfaceShortcuts = {
  mode: 'key', ignoresShift: false,
  keys: new Set(['Y', 'N', 'C', 'S', 'U', 'E', 'F', '?', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']),
}
const NONE: SurfaceShortcuts = { mode: 'code', ignoresShift: true, keys: new Set() }

export const SURFACE_SHORTCUTS: Readonly<Record<ActivitySurface, SurfaceShortcuts>> = {
  app: NONE,
  task_list: NONE,
  segment_editing: SEGMENT_EDITING,
  quick_edit: QUICK_EDIT,
  markup: MARKUP,
  group_review: GROUP_REVIEW,
}

const NAMED = new Set(['Space', 'Enter', 'Escape', 'Delete', 'Backspace', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'])

type KeyEventLike = Pick<KeyboardEvent, 'code' | 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>

function baseKey(e: KeyEventLike, mode: ShortcutMatchMode): string | null {
  if (e.key === '?') return '?'
  if (mode === 'code') {
    const c = e.code
    if (/^Key[A-Z]$/.test(c)) return c.slice(3)
    // Only the digit row; numpad digits do not count.
    if (/^Digit[0-9]$/.test(c)) return c.slice(5)
    if (c === 'Comma') return ','
    if (c === 'Period') return '.'
    if (c === 'NumpadEnter') return 'Enter'
    if (NAMED.has(c)) return c
    if (c === 'Slash' && e.shiftKey) return '?'
    return null
  }
  const k = e.key
  if (k === ' ') return 'Space'
  if (NAMED.has(k)) return k
  if (k.length === 1 && /^[\x21-\x7E]$/.test(k)) return k.toUpperCase()
  return null
}

/**
 * The normalised name of a key press, matching `^[\x21-\x7E]{1,24}$`: `Ctrl+`
 * (Ctrl or Meta, which the editors treat alike), then `Alt+`, then `Shift+`, then
 * the base key — from `event.code` in `code` mode (`KeyR` -> `R`, `Comma` -> `,`), from
 * `event.key` in `key` mode (`y` -> `Y`). Shift is dropped from a plain letter or
 * digit (or `,` `.`) with no Ctrl/Alt when `ignoresShift`; `?` carries no prefix. Null for a key
 * that has no stable name (dead keys, `Unidentified`, …).
 */
export function normalizeShortcut(e: KeyEventLike, mode: ShortcutMatchMode, ignoresShift: boolean): string | null {
  const base = baseKey(e, mode)
  if (!base) return null
  if (base === '?') return '?'
  const ctrl = e.ctrlKey || e.metaKey
  const plainChar = /^[A-Z0-9,.]$/.test(base)
  const shift = e.shiftKey && !(ignoresShift && plainChar && !ctrl && !e.altKey)
  const name = `${ctrl ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${shift ? 'Shift+' : ''}${base}`
  return name.length <= 24 ? name : null
}

/** The normalised key if it is registered for `surface`, else null. */
export function registeredShortcut(e: KeyEventLike, surface: ActivitySurface): string | null {
  const reg = SURFACE_SHORTCUTS[surface]
  const name = normalizeShortcut(e, reg.mode, reg.ignoresShift)
  return name !== null && reg.keys.has(name) ? name : null
}

const ACTIVATION_TARGET = 'button, a[href], [role="button"], [role="menuitem"], summary, task-list-item'

/**
 * Enter or Space on a control: the browser (or the component) turns it into the
 * click that the click listener counts and whose action is tracked with its own
 * trigger, so it is neither a shortcut nor "ignored".
 */
export function isActivationKeyOnControl(e: Pick<KeyboardEvent, 'key' | 'code' | 'target'>): boolean {
  if (e.key !== 'Enter' && e.code !== 'Space' && e.key !== ' ') return false
  const target = e.target
  return target instanceof Element && target.closest(ACTIVATION_TARGET) !== null
}
